/**
 * Milkdown Plugin to track active marks in selection
 *
 * This plugin replaces inefficient polling (setInterval 100ms) with an
 * event-driven system that only updates stores when selection changes.
 *
 * Performance: O(1) instead of O(n) every 100ms
 */

import { $prose } from "@milkdown/kit/utils";
import { Plugin, PluginKey } from "@milkdown/kit/prose/state";
import type { EditorState } from "@milkdown/kit/prose/state";
import type { EditorView } from "@milkdown/kit/prose/view";
import type { Node as ProseMirrorNode } from "@milkdown/kit/prose/model";
import {
  activeMarks,
  activeNode,
  type ActiveMarksData,
  type ActiveNodeData,
} from "../stores/editorStore";

/**
 * Unique key for the ProseMirror plugin
 */
export const selectionPluginKey = new PluginKey("selection-tracker");

/**
 * Mapping of Milkdown mark names to store names
 */
const MARK_MAPPING: Record<string, keyof ActiveMarksData> = {
  strong: "bold",
  emphasis: "italic",
  strikethrough: "strikethrough",
  inlineCode: "code",
};

/**
 * Analyzes selection and returns active marks
 */
function analyzeMarks(state: EditorState): ActiveMarksData {
  const { from, to, empty } = state.selection;

  const result: ActiveMarksData = {
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
  };

  if (empty) {
    // No selection: check marks at cursor
    const storedMarks = state.storedMarks || state.selection.$from.marks();
    for (const mark of storedMarks) {
      const mappedName = MARK_MAPPING[mark.type.name];
      if (mappedName) {
        result[mappedName] = true;
      }
    }
  } else {
    // Selection: check marks in range
    state.doc.nodesBetween(from, to, (node: ProseMirrorNode) => {
      if (node.marks) {
        for (const mark of node.marks) {
          const mappedName = MARK_MAPPING[mark.type.name];
          if (mappedName) {
            result[mappedName] = true;
          }
        }
      }
    });
  }

  return result;
}

/**
 * Analyzes selection and returns active node type
 */
function analyzeNode(state: EditorState): ActiveNodeData {
  const { $from } = state.selection;

  // Walk up the tree to find the parent block node
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    const nodeName = node.type.name;

    // Headings
    if (nodeName === "heading") {
      return {
        type: "heading",
        level: (node.attrs.level as number) || 1,
      };
    }

    // Lists
    if (nodeName === "bullet_list" || nodeName === "bulletList") {
      return { type: "bulletList", level: null };
    }
    if (nodeName === "ordered_list" || nodeName === "orderedList") {
      return { type: "orderedList", level: null };
    }

    // Blockquote
    if (nodeName === "blockquote") {
      return { type: "blockquote", level: null };
    }

    // Code block
    if (nodeName === "code_block" || nodeName === "codeBlock") {
      return { type: "codeBlock", level: null };
    }
  }

  // Default: paragraph
  return { type: "paragraph", level: null };
}

/**
 * Compares two mark objects to detect changes
 */
function marksChanged(prev: ActiveMarksData, next: ActiveMarksData): boolean {
  return (
    prev.bold !== next.bold ||
    prev.italic !== next.italic ||
    prev.strikethrough !== next.strikethrough ||
    prev.code !== next.code
  );
}

/**
 * Compares two node objects to detect changes
 */
function nodeChanged(prev: ActiveNodeData, next: ActiveNodeData): boolean {
  return prev.type !== next.type || prev.level !== next.level;
}

/**
 * Creates the ProseMirror plugin for selection tracking
 */
function createSelectionPlugin(): Plugin {
  let lastMarks: ActiveMarksData = {
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
  };
  let lastNode: ActiveNodeData = { type: "paragraph", level: null };

  return new Plugin({
    key: selectionPluginKey,

    view() {
      return {
        update(view: EditorView, prevState: EditorState) {
          const { state } = view;

          // Check if selection or document has changed
          const selectionChanged = !prevState.selection.eq(state.selection);
          const docChanged = !prevState.doc.eq(state.doc);

          if (!selectionChanged && !docChanged) {
            return;
          }

          // Analyze active marks
          const newMarks = analyzeMarks(state);
          if (marksChanged(lastMarks, newMarks)) {
            lastMarks = newMarks;
            activeMarks.set(newMarks);
          }

          // Analyze active node
          const newNode = analyzeNode(state);
          if (nodeChanged(lastNode, newNode)) {
            lastNode = newNode;
            activeNode.set(newNode);
          }
        },

        destroy() {
          // Reset stores when editor is destroyed
          activeMarks.set({
            bold: false,
            italic: false,
            strikethrough: false,
            code: false,
          });
          activeNode.set({ type: "paragraph", level: null });
        },
      };
    },
  });
}

/**
 * Exportable Milkdown plugin
 * Uses $prose to integrate the ProseMirror plugin into Milkdown
 */
export const selectionPlugin = $prose(() => createSelectionPlugin());

export default selectionPlugin;
